import React from 'react';
import { DriveFileRenameOutline } from '@mui/icons-material';
import { coSupervisorAllFilter, coSupervisorToDoFilter } from '../../filters';
import {
  ApplicationViewList,
  type CustomAction,
  type ApplicationViewListProps,
  type FilterFields,
  type TableColumns,
  type ApplicationViewTableDataItem,
} from '../../../../Application/components';
import { COMMON_ACTION } from '../../../../Application/components/ApplicationViewList/constants';
import { useAuth } from '../../../../../contexts';
import { APPLICATION_STATUS_TYPE } from '../../../../../utils/statusConstants/statusConstants';
import { SupervisorRoleTypes } from '../../../constants';

export const CoSupervisorList: React.FunctionComponent = () => {
  const { user } = useAuth();

  const quickFilters = [
    {
      id: 'TO_DO',
      text: 'TO-DO',
      filter: coSupervisorToDoFilter(user),
      isDefault: true,
    },
    { id: 'ALL', text: 'ALL', filter: coSupervisorAllFilter(user) },
  ];

  // Enable department filter for supervisor application list
  // As some supervisors are joint-appointed by multiple departments
  const hiddenFilters: FilterFields[] = React.useMemo(
    () => [
      'applicantLocalIndicator',
      'scholarshipAppliedByApplicant',
      'applicationStatus',
      'fundingSchemeStatus',
      'fundingScheme',
      'hkpfsNo',
      'dualPartnerUniversity',
      'jointpartnerUniversity',
      'partnerCategory',
      'gsAssignee',
    ],
    [],
  );

  const handleGoDeclaration = (row: ApplicationViewTableDataItem): void => {
    const { applicationId, nominationId } = row;
    if (!nominationId) return;
    window.open(`/nomination/co/${applicationId}/${nominationId}`);
  };

  const customActions: CustomAction[] = [
    {
      icon: <DriveFileRenameOutline />,
      handleAction: handleGoDeclaration,
      checkDisable: row => {
        const status = row.applicationView.status;
        const nominationSubmitted =
          row.applicationView.nomination.submission.submitted;
        const supervisor = row.applicationView.nomination.supervisors.find(
          (s: any) => {
            return (
              s.type === SupervisorRoleTypes.CO_SUPERVISOR &&
              s.netId === user?.sub
            );
          },
        );
        const declarationFinished =
          supervisor?.confirmedAsCoSupervisor ?? false;
        return (
          !(
            status === APPLICATION_STATUS_TYPE.UNDER_SUPERVISOR &&
            nominationSubmitted
          ) || declarationFinished
        );
      },
    },
  ];

  const visibleColumns: TableColumns[] = React.useMemo(
    () => [
      'applicationNo',
      'applicantName',
      'applicantEmail',
      'department',
      'applicantNationality',
      'entryYear',
      'applyProgramme',
      'researchAreas',
      'nominatedFundings',
      'applicationStatus',
      'applicationSubmissionTime',
    ],
    [],
  );

  const filterProps: ApplicationViewListProps['filterProps'] = {
    hiddenFilterFields: hiddenFilters,
  };

  const toolsProps: ApplicationViewListProps['toolsProps'] = {
    enableExport: false,
  };

  const quickFilterProps: ApplicationViewListProps['quickFilterProps'] = {
    quickFilters,
  };

  const tableProps: ApplicationViewListProps['tableProps'] = {
    initialVisibleColumns: visibleColumns,
    pickCommonActions: [COMMON_ACTION.MONITOR],
    customActions,
  };

  return (
    <ApplicationViewList
      tableProps={tableProps}
      filterProps={filterProps}
      toolsProps={toolsProps}
      quickFilterProps={quickFilterProps}
    />
  );
};
